import React from 'react';
import {
    Alert,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardHeader,
    Col,
    Input,
    Row,
    Table,
} from 'reactstrap';
import {productSearchOverrideService} from '../services/productSearchOverride.service';

const PORTALS = [
    {value: 'energex', label: 'Energex'},
];

const PRODUCT_TYPES = [
    {value: 'inverter', label: 'Inverter'},
    {value: 'module', label: 'Module'},
    {value: 'battery', label: 'Battery'},
];

class ProductSearchOverrides extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: null,
            portal: 'energex',
            productType: 'inverter',
            products: [],
            editedOverrides: {},
            savingIds: {},
        };
    }

    componentDidMount() {
        this.loadProducts();
    }

    loadProducts = () => {
        const {portal, productType} = this.state;
        this.setState({loading: true, error: null});

        productSearchOverrideService.getProductsByType(productType, portal)
            .then(response => {
                if (response.success) {
                    this.setState({
                        loading: false,
                        products: response.data || [],
                        editedOverrides: {},
                    });
                } else {
                    this.setState({
                        loading: false,
                        error: response.error || 'Failed to load products',
                    });
                }
            })
            .catch(err => {
                this.setState({
                    loading: false,
                    error: err.message || 'Failed to load products',
                });
            });
    };

    handlePortalChange = (e) => {
        this.setState({portal: e.target.value}, this.loadProducts);
    };

    handleTypeChange = (type) => {
        this.setState({productType: type}, this.loadProducts);
    };

    handleOverrideChange = (externalId, value) => {
        this.setState(prevState => ({
            editedOverrides: {
                ...prevState.editedOverrides,
                [externalId]: value,
            },
        }));
    };

    getDisplayValue = (product) => {
        const {editedOverrides} = this.state;
        if (editedOverrides.hasOwnProperty(product.external_id)) {
            return editedOverrides[product.external_id];
        }
        return product.override_term || '';
    };

    hasChanges = (product) => {
        const {editedOverrides} = this.state;
        if (!editedOverrides.hasOwnProperty(product.external_id)) {
            return false;
        }
        const currentValue = product.override_term || '';
        return editedOverrides[product.external_id] !== currentValue;
    };

    handleSave = (product) => {
        const {portal, editedOverrides} = this.state;
        const newValue = editedOverrides[product.external_id];

        this.setState(prevState => ({
            savingIds: {...prevState.savingIds, [product.external_id]: true},
        }));

        const savePromise = newValue && newValue.trim()
            ? productSearchOverrideService.saveOverride(product.external_id, portal, newValue.trim())
            : productSearchOverrideService.deleteOverride(product.external_id, portal);

        savePromise
            .then(() => {
                this.setState(prevState => {
                    const newEdited = {...prevState.editedOverrides};
                    delete newEdited[product.external_id];
                    const newSaving = {...prevState.savingIds};
                    delete newSaving[product.external_id];
                    return {editedOverrides: newEdited, savingIds: newSaving};
                });
                this.loadProducts();
            })
            .catch(err => {
                this.setState(prevState => {
                    const newSaving = {...prevState.savingIds};
                    delete newSaving[product.external_id];
                    return {savingIds: newSaving, error: err.message};
                });
            });
    };

    handleClear = (product) => {
        const {portal} = this.state;

        this.setState(prevState => ({
            savingIds: {...prevState.savingIds, [product.external_id]: true},
        }));

        productSearchOverrideService.deleteOverride(product.external_id, portal)
            .then(() => {
                this.setState(prevState => {
                    const newEdited = {...prevState.editedOverrides};
                    delete newEdited[product.external_id];
                    const newSaving = {...prevState.savingIds};
                    delete newSaving[product.external_id];
                    return {editedOverrides: newEdited, savingIds: newSaving};
                });
                this.loadProducts();
            })
            .catch(err => {
                this.setState(prevState => {
                    const newSaving = {...prevState.savingIds};
                    delete newSaving[product.external_id];
                    return {savingIds: newSaving, error: err.message};
                });
            });
    };

    render() {
        const {loading, error, portal, productType, products, savingIds} = this.state;

        return (
            <div className="content">
                <Row>
                    <Col>
                        <Card>
                            <CardHeader>
                                <h4>Product Search Overrides</h4>
                            </CardHeader>
                            <CardBody>
                                {error && <Alert color="danger">{error}</Alert>}

                                <Row className="mb-3">
                                    <Col md="3">
                                        <label>Portal</label>
                                        <Input
                                            type="select"
                                            value={portal}
                                            onChange={this.handlePortalChange}
                                        >
                                            {PORTALS.map(p => (
                                                <option key={p.value} value={p.value}>
                                                    {p.label}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                    <Col md="6">
                                        <label>Product Type</label>
                                        <br />
                                        <ButtonGroup>
                                            {PRODUCT_TYPES.map(t => (
                                                <Button
                                                    key={t.value}
                                                    color={productType === t.value ? 'primary' : 'secondary'}
                                                    onClick={() => this.handleTypeChange(t.value)}
                                                >
                                                    {t.label}
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </Col>
                                </Row>

                                {loading && <Alert color="info">Loading products...</Alert>}

                                {!loading && products.length === 0 && (
                                    <Alert color="warning">No products found for this type.</Alert>
                                )}

                                {!loading && products.length > 0 && (
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th>Manufacturer</th>
                                                <th>Product Name</th>
                                                <th>Product Code</th>
                                                <th>Search Override</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.map(product => (
                                                <tr key={product.external_id}>
                                                    <td>{product.manufacturer}</td>
                                                    <td>{product.product_name}</td>
                                                    <td>{product.product_code}</td>
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            value={this.getDisplayValue(product)}
                                                            onChange={(e) => this.handleOverrideChange(product.external_id, e.target.value)}
                                                            placeholder="Enter search term override..."
                                                            disabled={savingIds[product.external_id]}
                                                        />
                                                    </td>
                                                    <td>
                                                        {this.hasChanges(product) && (
                                                            <Button
                                                                color="success"
                                                                size="sm"
                                                                onClick={() => this.handleSave(product)}
                                                                disabled={savingIds[product.external_id]}
                                                            >
                                                                {savingIds[product.external_id] ? 'Saving...' : 'Save'}
                                                            </Button>
                                                        )}
                                                        {' '}
                                                        {product.has_override && !this.hasChanges(product) && (
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => this.handleClear(product)}
                                                                disabled={savingIds[product.external_id]}
                                                            >
                                                                {savingIds[product.external_id] ? 'Clearing...' : 'Clear'}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default ProductSearchOverrides;
